import Sentry from '@/services/sentry/Sentry';
import {downloadCsvFile} from '@/utils/downloadAuthenticatedCsv';
import {
  SocialMediaPost,
  SocialMediaPostSchema,
  RawSocialMediaPost,
  SocialStream,
  SocialStreamTab,
  CampaignStatsMetadata,
  PaginationMeta,
  CampaignStatsMetadataSchema,
  PaginationMetaSchema,
} from '@/types/socialMediaPost';
import api from '../index';

/**
 * Query parameters for fetching social media posts
 */
interface SocialMediaPostsQueryParams {
  include: string;
  page?: number;
  per_page?: number;
  sort?: string;
  q?: string;
  social_stream?: SocialStream;
  start_date?: string;
  end_date?: string;
}

/**
 * Query parameters for fetching campaign metrics
 */
interface CampaignMetricsQueryParams {
  social_stream: SocialStreamTab;
  start_date?: string;
  end_date?: string;
}

/**
 * Raw post data from the API before transformation
 *


/**
 * Transforms raw API post data to validated SocialMediaPost
 * Normalizes nullable fields and validates with Zod schema
 * @returns Validated post or null if validation fails
 */
function transformPost(rawPost: RawSocialMediaPost): SocialMediaPost | null {
  // Normalize the post - only nullable fields need fallbacks
  // Required fields (DB enforced) are passed through directly
  const normalizedPost = {
    id: rawPost.id,
    // Nullable fields - normalize undefined to null
    body: rawPost.body ?? null,
    clickthroughCount: rawPost.clickthroughCount ?? null,
    closeupImpressionCount: rawPost.closeupImpressionCount ?? null,
    createdAt: rawPost.createdAt ?? '',
    engagementRate: rawPost.engagementRate ?? null,
    isTopPerformerByEngagement: rawPost.isTopPerformerByEngagement ?? null,
    likeCount: rawPost.likeCount ?? null,
    link: rawPost.link ?? null,
    postedAt: rawPost.postedAt ?? null,
    repostCount: rawPost.repostCount ?? null,
    socialStream: rawPost.socialStream,
    thumbnailUrl: rawPost.thumbnailUrl ?? null,
    url: rawPost.url ?? '',
    viewCount: rawPost.viewCount ?? null,
    // Required fields - DB enforces these, pass through directly
    commentCount: rawPost.commentCount,
    engagementRateOrganic: rawPost.engagementRateOrganic,
    followerCount: rawPost.followerCount,
    impressionCount: rawPost.impressionCount,
    reachCount: rawPost.reachCount,
    replyCount: rawPost.replyCount,
    savedCount: rawPost.savedCount,
    shareCount: rawPost.shareCount,
    totalInteractionsCount: rawPost.totalInteractionsCount,
    videoAggregatedPlaysCount: rawPost.videoAggregatedPlaysCount,
    videoAverageWatchTime: rawPost.videoAverageWatchTime ?? null,
    videoViewsOrganic: rawPost.videoViewsOrganic,
    videoViewsPaid: rawPost.videoViewsPaid,
    emv: rawPost.emv ?? null,
    favoritesOrganic: rawPost.favoritesOrganic ?? null,
    favoritesPaid: rawPost.favoritesPaid ?? null,
    // Optional relationship - pass through all fields, Zod schema handles extraction
    profile: rawPost.profile
      ? {
          ...rawPost.profile,
          displayName: rawPost.profile.displayName ?? null,
          imageUrl: rawPost.profile.imageUrl ?? null,
          firstName: rawPost.profile.firstName ?? null,
          lastName: rawPost.profile.lastName ?? null,
        }
      : undefined,
  };

  // Validate and parse with Zod schema
  const result = SocialMediaPostSchema.safeParse(normalizedPost);

  if (!result.success) {
    const errors = result.error.errors.map((error) => error.message).join(', ');
    const errorMessage = `Social media post validation failed: ${rawPost.id} with errors: ${errors} for post: ${JSON.stringify(normalizedPost, null, 2)}`;
    // Report validation error to Sentry for monitoring
    Sentry.addBreadcrumb({
      message: errorMessage,
      level: 'error',
      type: 'error',
    });

    // Return null to filter out invalid posts
    return null;
  }

  return result.data;
}

/**
 * Response structure from the API after transformation
 */
export interface CampaignSocialMediaPostsResponse {
  posts: SocialMediaPost[];
  campaignStats: CampaignStatsMetadata | undefined;
  pagination: PaginationMeta;
}

/**
 * Parameters for fetching campaign social media posts
 */
export interface CampaignSocialMediaPostsParams {
  campaignId: string;
  page?: number;
  perPage?: number;
  sort?: string;
  q?: string;
  socialStream?: SocialStream;
  startDate?: string;
  endDate?: string;
}

/**
 * Fetches social media posts for a campaign
 * @param params - Parameters for the request
 * @returns Transformed response with posts, campaign stats, and pagination
 */
async function fetchCampaignSocialMediaPosts(
  params: CampaignSocialMediaPostsParams
): Promise<CampaignSocialMediaPostsResponse> {
  const {campaignId, page, perPage, sort, q, socialStream, startDate, endDate} =
    params;

  if (!campaignId) {
    throw new Error('campaignId is required');
  }

  // Build query params
  const queryParams: SocialMediaPostsQueryParams = {
    include: 'profile',
    ...(page !== undefined && {page}),
    ...(perPage !== undefined && {per_page: perPage}),
    ...(sort && {sort}),
    ...(q && {q}),
    ...(socialStream && {social_stream: socialStream}),
    ...(startDate && {start_date: startDate}),
    ...(endDate && {end_date: endDate}),
  };

  const response = await api.get(
    `/campaigns/${campaignId}/social_media_posts`,
    {
      params: queryParams,
    }
  );

  // Transform and validate posts using Zod schema
  // Filter out any posts that fail validation (they're reported to Sentry)
  const posts: SocialMediaPost[] = (response.data || [])
    .map(transformPost)
    .filter(
      (post: SocialMediaPost | null): post is SocialMediaPost => post !== null
    );

  const rawCampaignStats = response.meta?.campaignStats;
  const parseCampaignStats =
    CampaignStatsMetadataSchema.safeParse(rawCampaignStats);
  let campaignStats: CampaignStatsMetadata | undefined;
  if (parseCampaignStats.success) {
    campaignStats = rawCampaignStats;
  } else {
    const errors = parseCampaignStats.error.errors
      .map((error) => error.message)
      .join(', ');
    const errorMessage = `Campaign stats validation failed for campaign ${campaignId} with errors: ${errors} for stats: ${JSON.stringify(rawCampaignStats, null, 2)}`;
    Sentry.addBreadcrumb({
      message: errorMessage,
      level: 'error',
      type: 'error',
    });
  }

  // Extract pagination from meta
  const parsePagination = PaginationMetaSchema.safeParse(
    response.meta?.pagination
  );
  let pagination: PaginationMeta | undefined;
  if (parsePagination.success) {
    pagination = parsePagination.data;
  } else {
    const errors = parsePagination.error.errors
      .map((error) => error.message)
      .join(', ');
    const errorMessage = `Pagination validation failed for campaign ${campaignId} with errors: ${errors} for pagination: ${JSON.stringify(response.meta?.pagination, null, 2)}`;
    Sentry.addBreadcrumb({
      message: errorMessage,
      level: 'error',
      type: 'error',
    });

    // If pagination metadata is missing but posts exist, use posts.length as minimum
    // This is better than 0, which would incorrectly show no posts
    if (posts.length) {
      // Log warning when pagination is missing but posts exist (unexpected scenario)
      Sentry.addBreadcrumb({
        message: `Missing pagination metadata for campaign ${campaignId} but posts were returned. Using inferred values.`,
        level: 'warning',
        type: 'error',
        data: {
          postsCount: posts.length,
          page,
          perPage,
        },
      });
    }

    pagination = {
      currentPage: page ?? 1,
      nextPage: null,
      prevPage: null,
      totalEntries: posts.length,
      totalPages: posts.length > 0 ? 1 : 0,
    };
  }

  return {
    posts,
    campaignStats,
    pagination,
  };
}

/**
 * Parameters for fetching campaign metrics
 */
export interface CampaignMetricsParams {
  campaignId: string;
  socialStream?: SocialStreamTab;
  startDate?: string;
  endDate?: string;
}

/**
 * Fetches campaign metrics/stats from the dedicated stats endpoint
 * @param params - Parameters for the request
 * @returns Campaign stats metadata
 */
async function fetchCampaignMetrics(
  params: CampaignMetricsParams
): Promise<CampaignStatsMetadata> {
  const {campaignId, socialStream = 'feed', startDate, endDate} = params;

  if (!campaignId) {
    throw new Error('campaignId is required');
  }

  const queryParams: CampaignMetricsQueryParams = {
    social_stream: socialStream,
    ...(startDate && {start_date: startDate}),
    ...(endDate && {end_date: endDate}),
  };

  const response = await api.get(`/campaigns/${campaignId}/stats`, {
    params: queryParams,
  });

  const parseCampaignStats = CampaignStatsMetadataSchema.safeParse(
    response.data
  );
  let data: CampaignStatsMetadata;
  if (parseCampaignStats.success) {
    data = parseCampaignStats.data;
  } else {
    const errors = parseCampaignStats.error.errors
      .map((error) => error.message)
      .join(', ');
    const errorMessage = `Campaign metrics validation failed for campaign ${campaignId} with errors: ${errors} for stats: ${JSON.stringify(response.data, null, 2)}`;
    Sentry.addBreadcrumb({
      message: errorMessage,
      level: 'error',
      type: 'error',
    });
    data = {
      totalsByNetwork: undefined,
      averagesByNetwork: undefined,
      postCount: 0,
      totalVideoViewsOrganic: 0,
      totalVideoViewsPaid: 0,
      organicStatsRefreshedAt: null,
      emvFormula: undefined,
    };
  }

  return {
    totalsByNetwork: data.totalsByNetwork,
    averagesByNetwork: data.averagesByNetwork,
    postCount: data.postCount,
    totalVideoViewsOrganic: data.totalVideoViewsOrganic,
    totalVideoViewsPaid: data.totalVideoViewsPaid,
    organicStatsRefreshedAt: data.organicStatsRefreshedAt ?? null,
    emvFormula: data.emvFormula,
  };
}

/**
 * Downloads the metrics CSV for campaign social media posts
 * @param campaignId - The campaign ID
 * @param socialStream - The social stream type (all, feed, story, reels).
 *   Always forwarded as the social_stream param. 'all' is required (not omitted)
 *   so the backend returns the union of all stream columns (e.g. average watch
 *   time); omitting it yields feed-only headers.
 */
function downloadMetricsCsv(
  campaignId: string,
  socialStream: SocialStreamTab
): Promise<void> {
  const date = new Date().toISOString().slice(0, 10);
  return downloadCsvFile(
    `/campaigns/${campaignId}/metrics.csv`,
    {social_stream: socialStream},
    `campaign-${campaignId}-${socialStream}-${date}.csv`
  );
}

const campaignSocialMediaPostsFetchers = {
  fetchPosts: fetchCampaignSocialMediaPosts,
  fetchMetrics: fetchCampaignMetrics,
  downloadMetricsCsv,
};

export default campaignSocialMediaPostsFetchers;
